import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cpApi } from '../api/api';
import type { WTRequest } from '../types';

interface ReceiptViewWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  wtRequestId: number | null;
  receiptFilePath?: string | null;
}

export const ReceiptViewWidget: React.FC<ReceiptViewWidgetProps> = ({
  isOpen,
  onClose,
  wtRequestId,
  receiptFilePath: initialReceiptPath,
}) => {
  const [currentRequest, setCurrentRequest] = useState<WTRequest | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);

  const loadRequest = useCallback(async () => {
    if (!wtRequestId) return;
    setIsLoadingRequest(true);
    setError(null);
    try {
      const req = await cpApi.getWTRequest(wtRequestId) as WTRequest | null;
      setCurrentRequest(req ?? null);
    } catch {
      setError('Не удалось загрузить данные заявки');
      setCurrentRequest(null);
    } finally {
      setIsLoadingRequest(false);
    }
  }, [wtRequestId]);

  const loadPreview = useCallback(async () => {
    if (!wtRequestId) return;
    setIsLoadingPreview(true);
    setError(null);
    try {
      const blob = await cpApi.downloadReceipt(wtRequestId);
      const mime = blob.type || ((initialReceiptPath ?? '').toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg');
      setIsPdf(mime === 'application/pdf');
      setFileSize(blob.size);
      const typedBlob = blob.type ? blob : new Blob([blob], { type: mime });
      const url = URL.createObjectURL(typedBlob);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch {
      setError('Не удалось загрузить предпросмотр');
      setPreviewUrl(null);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [wtRequestId, initialReceiptPath]);

  useEffect(() => {
    if (isOpen && wtRequestId) {
      loadRequest();
    }
  }, [isOpen, wtRequestId, loadRequest]);

  useEffect(() => {
    if (isOpen && wtRequestId) {
      loadPreview();
    }
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, wtRequestId]);

  useEffect(() => {
    if (!isOpen) {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      setCurrentRequest(null);
      setError(null);
      setFileSize(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setError(null);
    setCurrentRequest(null);
    onClose();
  };

  const handleDownload = async () => {
    if (!wtRequestId) return;
    setIsDownloading(true);
    try {
      const blob = await cpApi.downloadReceipt(wtRequestId);
      const path = initialReceiptPath ?? '';
      const ext = path.toLowerCase().endsWith('.pdf')
        ? '.pdf'
        : (path.match(/\.[a-z0-9]+$/i)?.[0] ?? (blob.type === 'application/pdf' ? '.pdf' : '.png'));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${wtRequestId}${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      setError('Ошибка при скачивании');
    } finally {
      setIsDownloading(false);
    }
  };

  const receiptReplaceAccept = useMemo(
    () => (currentRequest?.allow_receipt_image_upload ? '.pdf,.png,.jpeg,.jpg' : '.pdf'),
    [currentRequest?.allow_receipt_image_upload]
  );

  const handleReplaceReceipt = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!wtRequestId || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const fileName = file.name.toLowerCase();
    const allowedExtensions = currentRequest?.allow_receipt_image_upload
      ? ['.pdf', '.png', '.jpeg', '.jpg']
      : ['.pdf'];
    const ext = fileName.substring(fileName.lastIndexOf('.'));
    if (!allowedExtensions.includes(ext)) {
      setError(
        currentRequest?.allow_receipt_image_upload
          ? 'Неподдерживаемый тип файла. Разрешены: PDF, PNG, JPEG'
          : 'Неподдерживаемый тип файла. Разрешён только PDF (скрины — если включены для команды в настройках).'
      );
      e.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Размер файла не должен превышать 10 МБ');
      e.target.value = '';
      return;
    }
    setIsUploadingReceipt(true);
    setError(null);
    try {
      const data = await cpApi.replaceReceipt(wtRequestId, file);
      await cpApi.resetReceiptOriginality(wtRequestId);
      await cpApi.updateReceiptCheckFlags(wtRequestId, {
        check_amount: false,
        check_recipient: false,
        check_card: false,
        check_bank: false,
        check_status: false,
      });
      setCurrentRequest((prev) =>
        prev ? { ...prev, receipt_file_path: data.file_path || prev.receipt_file_path } : null
      );
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      await loadRequest(); // подтянуть receipt_replaced_at/method из истории
      await loadPreview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при замене документа');
    } finally {
      setIsUploadingReceipt(false);
      e.target.value = '';
    }
  };

  if (!isOpen || !wtRequestId) return null;

  const receiptPath = currentRequest?.receipt_file_path ?? initialReceiptPath;
  const isPdfFile = isPdf || receiptPath?.toLowerCase?.()?.endsWith('.pdf');

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[300] p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-6xl w-full overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: 'calc(98vh)', minHeight: '82vh' }}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700/60 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Просмотр чека — Заявка #{wtRequestId}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Центр: предпросмотр */}
          <div className="flex-1 bg-gray-100 dark:bg-gray-950 p-4 overflow-auto min-h-0">
            {isLoadingRequest ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-gray-500 dark:text-gray-400">Загрузка...</div>
              </div>
            ) : isLoadingPreview ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
              </div>
            ) : previewUrl ? (
              <div className="h-full flex items-stretch justify-center">
                {isPdfFile ? (
                  <iframe
                    src={previewUrl}
                    className="w-full h-full min-h-[800px] border border-gray-300 dark:border-gray-700 rounded-lg bg-white"
                    title="Предпросмотр чека"
                  />
                ) : (
                  <img
                    key={previewUrl}
                    src={previewUrl}
                    className="max-w-full max-h-full border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg object-contain bg-white"
                    alt="Чек"
                    onError={() => setError('Ошибка отображения изображения')}
                  />
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                {error || 'Предпросмотр недоступен'}
              </div>
            )}
          </div>

          {/* Правая панель: информация и кнопки */}
          <div className="w-80 border-l border-gray-200 dark:border-gray-700/60 overflow-y-auto p-4 dark:bg-gray-900 flex-shrink-0">
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">Тип файла:</span>
                  <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                    {isPdfFile ? 'PDF' : 'Изображение'}
                  </div>
                  {isPdfFile && fileSize != null && (
                    <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {(fileSize / 1024).toFixed(2)} КБ
                    </div>
                  )}
                </div>
                {currentRequest?.receipt_replaced_at && (
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">Чек перезагружен:</span>
                    <div className="mt-1 text-sm text-gray-700 dark:text-gray-200">
                      <div className="text-sm font-medium text-orange-700 dark:text-orange-200">
                        {(() => {
                          const methodLabels: Record<string, string> = {
                            merchant: 'Мерчантом',
                            admin: 'Админом',
                            counterparty: 'Контрагентом',
                          };
                          return methodLabels[currentRequest.receipt_replaced_by_method || ''] || currentRequest.receipt_replaced_by_method || 'Неизвестно';
                        })()}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 dark:text-gray-400">
                        {new Date(currentRequest.receipt_replaced_at).toLocaleString('ru-RU', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={loadPreview}
                disabled={isLoadingPreview}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Перезагрузить
              </button>
              <button
                onClick={handleDownload}
                disabled={isDownloading || isLoadingPreview}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4 4m4 4V4" />
                </svg>
                {isDownloading ? 'Скачивание...' : 'Скачать чек'}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                accept={receiptReplaceAccept}
                onChange={handleReplaceReceipt}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingReceipt || isLoadingPreview}
                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {isUploadingReceipt ? 'Загрузка...' : 'Заменить документ'}
              </button>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-lg">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
